// web/src/screens/Dns.tsx — the AI DNS tab (DNS_AI_AGENT_SPEC §4f). Reached only inside a site
// context (/dns?site=<owned>): records table + plain-English ask box + "paste your DNS" + the AI's
// proposed plan as a diff (approve → confirm for destructive) + change history with one-click revert.
// Every call is scoped server-side to the site's own zone; the client never sends a zone id.
import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { DnsPlanDto, DnsRecordDto, DnsDiffRow } from '../lib/types';
import { PageHeader } from '../components/PageHeader';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { Skeleton } from '../components/Skeleton';
import { DataTable } from '../components/DataTable';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { toast } from '../components/Toast';

export function Dns() {
  const [params] = useSearchParams();
  const site = params.get('site');
  return site ? <SiteDns siteId={site} /> : <DnsPicker />;
}

// ── No site chosen: a small index of sites that have a connected custom domain ──────────────────
function DnsPicker() {
  const q = useQuery({ queryKey: ['sites'], queryFn: () => api.listSites() });
  return (
    <>
      <PageHeader title="Domains & DNS" subtitle="Pick a site to manage its DNS with the AI assistant." />
      <AsyncBoundary query={q} skeleton={<Skeleton variant="card" count={3} />}>
        {(sites) => {
          const withDomain = sites.filter((s) => s.url && !/yourproject\.ai$/.test(s.url.replace(/^https?:\/\//, '').replace(/\/.*$/, '')));
          if (withDomain.length === 0) {
            return <EmptyState icon="globe" title="No custom domains yet"
              body="Connect a custom domain to a site (from its page) to manage DNS here." />;
          }
          return (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {withDomain.map((s) => (
                <Link key={s.id} to={`/dns?site=${s.id}`}
                  className="flex items-center gap-3 rounded-lg border border-hair bg-surface p-4 transition-transform hover:border-surface2 active:scale-[0.99]">
                  <span className="flex size-9 items-center justify-center rounded-md bg-surface2 text-info"><Icon name="globe" size={18} /></span>
                  <span className="flex-1">
                    <span className="block font-medium text-ink">{s.name}</span>
                    <span className="block text-xs text-muted">{s.url?.replace(/^https?:\/\//, '')}</span>
                  </span>
                  <Icon name="chevron-right" size={18} className="text-muted" />
                </Link>
              ))}
            </div>
          );
        }}
      </AsyncBoundary>
    </>
  );
}

// ── A single site's DNS panel ───────────────────────────────────────────────────────────────────
function SiteDns({ siteId }: { siteId: string }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const dns = useQuery({ queryKey: ['dns', siteId], queryFn: () => api.getDns(siteId) });
  const history = useQuery({ queryKey: ['dns-history', siteId], queryFn: () => api.dnsHistory(siteId) });

  const [intent, setIntent] = useState('');
  const [pasted, setPasted] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [plan, setPlan] = useState<DnsPlanDto | null>(null);
  const [confirmApply, setConfirmApply] = useState(false);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['dns', siteId] });
    qc.invalidateQueries({ queryKey: ['dns-history', siteId] });
  };

  const propose = useMutation({
    mutationFn: () => api.planDns(siteId, { intent: intent.trim() || undefined, pastedDns: pasted.trim() || undefined }),
    onSuccess: (p) => setPlan(p),
    onError: (e: unknown) => toast({ kind: 'error', message: e instanceof Error ? e.message : 'Could not propose changes' }),
  });

  const apply = useMutation({
    mutationFn: (confirmed: boolean) => api.applyDns(siteId, plan!.changeSetId, confirmed),
    onSuccess: () => {
      toast({ kind: 'success', message: 'DNS changes applied.' });
      setPlan(null); setIntent(''); setPasted(''); setConfirmApply(false); refresh();
    },
    onError: (e: unknown) => { setConfirmApply(false); toast({ kind: 'error', message: e instanceof Error ? e.message : 'Could not apply' }); },
  });

  const reject = useMutation({
    mutationFn: () => api.rejectDns(siteId, plan!.changeSetId),
    onSuccess: () => { setPlan(null); refresh(); toast({ kind: 'info', message: 'Plan discarded.' }); },
  });

  const revert = useMutation({
    mutationFn: (csId: string) => api.revertDns(siteId, csId),
    onSuccess: () => { refresh(); toast({ kind: 'success', message: 'Reverted to the previous DNS.' }); },
    onError: (e: unknown) => toast({ kind: 'error', message: e instanceof Error ? e.message : 'Could not revert' }),
  });

  const onApprove = () => { if (plan?.destructive) setConfirmApply(true); else apply.mutate(false); };

  return (
    <>
      <button onClick={() => nav(`/site?id=${siteId}`)} className="mb-4 inline-flex items-center gap-1 text-sm text-muted hover:text-ink">
        <Icon name="arrow-left" size={16} />Back to site
      </button>

      <AsyncBoundary query={dns} skeleton={<Skeleton variant="card" count={3} />}>
        {(data) => {
          if (!data.zoneActive) {
            return (
              <>
                <PageHeader title="DNS" subtitle="Manage this site's domain records." />
                <EmptyState icon="globe" title="No custom domain connected yet"
                  body="The DNS assistant manages a site's own Cloudflare zone, which exists once you connect a custom domain. Until then this site runs on its yourproject.ai address." />
              </>
            );
          }
          return (
            <>
              <PageHeader title="DNS" subtitle={data.domain} />

              {/* Records table */}
              <section className="mb-7">
                <h2 className="mb-3 font-head text-sm text-muted">Current records</h2>
                <DataTable
                  rows={data.records}
                  rowKey={(r) => r.id}
                  columns={[
                    { key: 'type', header: 'Type', render: (r) => <span className="font-head text-xs">{r.type}</span> },
                    { key: 'name', header: 'Name', render: (r) => <span className="break-all">{r.name}</span> },
                    { key: 'content', header: 'Content', render: (r) => <span className="break-all text-muted">{r.priority != null ? `${r.priority} ` : ''}{r.content}</span> },
                    { key: 'proxied', header: 'Proxy', render: (r) => <ProxyPill on={r.proxied} type={r.type} /> },
                    { key: 'ttl', header: 'TTL', render: (r) => <span className="text-muted">{r.ttl === 1 ? 'Auto' : r.ttl}</span> },
                  ]}
                  mobileCard={(r) => <RecordCard r={r} />}
                />
              </section>

              {/* Ask box */}
              <section className="mb-7 rounded-lg border border-hair bg-surface p-4">
                <h2 className="mb-1 font-head text-sm text-ink">Ask the DNS assistant</h2>
                <p className="mb-3 text-xs text-muted">Describe what you want in plain English. The assistant proposes exact changes — nothing is applied until you approve.</p>
                <textarea value={intent} onChange={(e) => setIntent(e.target.value)} rows={3}
                  placeholder="e.g. Point my domain at my new site and keep my Google email working."
                  className="w-full resize-y rounded-md border border-hair bg-bg px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-info focus:outline-none" />

                <button onClick={() => setShowPaste((v) => !v)} className="mt-2 inline-flex items-center gap-1 text-xs text-info">
                  <Icon name={showPaste ? 'x' : 'plus'} size={13} />{showPaste ? 'Hide paste box' : 'Or paste your current DNS (zone file / table)'}
                </button>
                {showPaste && (
                  <textarea value={pasted} onChange={(e) => setPasted(e.target.value)} rows={5}
                    placeholder={'@        A     203.0.113.10\nwww      CNAME example.com\n@        MX    1 aspmx.l.google.com'}
                    className="mt-2 w-full resize-y rounded-md border border-hair bg-bg px-3 py-2 font-mono text-xs text-ink placeholder:text-muted focus:border-info focus:outline-none" />
                )}

                <div className="mt-3 flex justify-end">
                  <button onClick={() => propose.mutate()} disabled={propose.isPending || (!intent.trim() && !pasted.trim())}
                    className="inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 font-medium text-runink transition-transform active:scale-[0.98] disabled:opacity-50">
                    <Icon name="send" size={16} />{propose.isPending ? 'Thinking…' : 'Propose changes'}
                  </button>
                </div>
              </section>

              {/* Plan review */}
              {plan && (
                <section className="mb-7 rounded-lg border border-info/40 bg-surface p-4">
                  <h2 className="mb-2 flex items-center gap-2 font-head text-sm text-ink"><Icon name="flask" size={16} className="text-info" />Proposed change</h2>
                  <p className="mb-3 text-sm text-muted">{plan.plan.summary}</p>

                  {plan.plan.warnings.length > 0 && (
                    <ul className="mb-3 flex flex-col gap-1">
                      {plan.plan.warnings.map((w, i) => (
                        <li key={i} className="flex items-start gap-2 rounded-md bg-warn/10 px-3 py-2 text-xs text-warn"><Icon name="alert" size={14} className="mt-0.5 shrink-0" />{w}</li>
                      ))}
                    </ul>
                  )}

                  {plan.diff.rows.length > 0 ? (
                    <div className="flex flex-col gap-2">
                      {plan.diff.rows.map((row, i) => <DiffRow key={i} row={row} />)}
                    </div>
                  ) : <p className="text-sm text-muted">No record changes — your DNS already matches.</p>}

                  {plan.diff.settings && (
                    <div className="mt-2 rounded-md border border-hair bg-bg px-3 py-2 text-xs text-muted">
                      Zone settings: {plan.diff.settings.ssl ? `SSL → ${plan.diff.settings.ssl}` : ''}{plan.diff.settings.always_use_https != null ? `${plan.diff.settings.ssl ? ', ' : ''}Always Use HTTPS → ${plan.diff.settings.always_use_https ? 'on' : 'off'}` : ''}
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                    <button onClick={() => reject.mutate()} disabled={reject.isPending}
                      className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-ink">
                      <Icon name="x" size={16} />Discard
                    </button>
                    <button onClick={onApprove} disabled={apply.isPending}
                      className="inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 font-medium text-runink transition-transform active:scale-[0.98] disabled:opacity-50">
                      <Icon name="check" size={16} />{apply.isPending ? 'Applying…' : plan.destructive ? 'Approve (needs confirm)' : 'Approve & apply'}
                    </button>
                  </div>
                </section>
              )}

              {/* History */}
              <section>
                <h2 className="mb-3 font-head text-sm text-muted">Change history</h2>
                <AsyncBoundary query={history} skeleton={<Skeleton variant="card" count={2} />}
                  isEmpty={(h) => h.length === 0}
                  empty={{ icon: 'clock', title: 'No changes yet', body: 'Proposed and applied DNS changes will appear here.' }}>
                  {(items) => (
                    <div className="flex flex-col gap-2">
                      {items.map((cs) => (
                        <div key={cs.id} className="flex items-start justify-between gap-3 rounded-lg border border-hair bg-surface p-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <CsStatus status={cs.status} />
                              <span className="text-xs text-muted">{cs.opCount} change{cs.opCount === 1 ? '' : 's'}</span>
                            </div>
                            <p className="mt-1 truncate text-sm text-ink">{cs.summary}</p>
                            {cs.intent && <p className="truncate text-xs text-muted">“{cs.intent}”</p>}
                          </div>
                          {cs.status === 'applied' && cs.hasSnapshot && (
                            <button onClick={() => revert.mutate(cs.id)} disabled={revert.isPending}
                              className="inline-flex min-h-9 shrink-0 items-center gap-1 rounded-md border border-hair px-3 text-xs text-ink">
                              <Icon name="refresh" size={14} />Revert
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </AsyncBoundary>
              </section>

              <ConfirmDialog open={confirmApply} title="Apply this destructive change?"
                body="This change deletes or replaces existing DNS records. If something depends on them (email, a service), it could stop working. You can revert from history afterwards."
                confirmLabel={apply.isPending ? 'Applying…' : 'Apply changes'} destructive
                onConfirm={() => apply.mutate(true)} onCancel={() => setConfirmApply(false)} />
            </>
          );
        }}
      </AsyncBoundary>
    </>
  );
}

function ProxyPill({ on, type }: { on: boolean; type: string }) {
  if (!['A', 'AAAA', 'CNAME'].includes(type)) return <span className="text-xs text-muted">—</span>;
  return on
    ? <span className="inline-flex items-center gap-1 rounded-pill border border-warn/40 px-2 py-0.5 text-[11px] text-warn"><Icon name="globe" size={12} />Proxied</span>
    : <span className="inline-flex items-center gap-1 rounded-pill border border-hair px-2 py-0.5 text-[11px] text-muted">DNS only</span>;
}

function RecordCard({ r }: { r: DnsRecordDto }) {
  return (
    <div className="rounded-lg border border-hair bg-surface p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="font-head text-xs text-ink">{r.type}</span>
        <ProxyPill on={r.proxied} type={r.type} />
      </div>
      <div className="mt-1 break-all text-sm text-ink">{r.name}</div>
      <div className="mt-0.5 break-all text-xs text-muted">{r.priority != null ? `${r.priority} ` : ''}{r.content}</div>
    </div>
  );
}

function DiffRow({ row }: { row: DnsDiffRow }) {
  const tone = row.kind === 'added' ? 'text-run border-run/40' : row.kind === 'removed' ? 'text-danger border-danger/40' : 'text-warn border-warn/40';
  const glyph = row.kind === 'added' ? 'plus' : row.kind === 'removed' ? 'x' : 'refresh';
  return (
    <div className={`rounded-md border bg-bg px-3 py-2 ${tone.split(' ')[1]}`}>
      <div className="flex items-center gap-2">
        <Icon name={glyph} size={13} className={tone.split(' ')[0]} />
        <span className={`font-head text-[11px] uppercase ${tone.split(' ')[0]}`}>{row.kind}</span>
        <span className="font-head text-xs text-ink">{row.type}</span>
        <span className="break-all text-xs text-ink">{row.name}</span>
      </div>
      <div className="mt-1 break-all text-xs text-muted">
        {row.before != null && <span className="line-through">{row.before}</span>}
        {row.before != null && row.after != null && <span className="px-1">→</span>}
        {row.after != null && <span className="text-ink">{row.after}</span>}
      </div>
      <div className="mt-0.5 text-[11px] text-muted">{row.reason}</div>
    </div>
  );
}

function CsStatus({ status }: { status: 'proposed' | 'applied' | 'rejected' | 'failed' }) {
  const map = {
    proposed: { label: 'Proposed', cls: 'text-info border-info/40', icon: 'clock' },
    applied: { label: 'Applied', cls: 'text-run border-run/40', icon: 'check' },
    rejected: { label: 'Discarded', cls: 'text-muted border-hair', icon: 'x' },
    failed: { label: 'Failed', cls: 'text-danger border-danger/40', icon: 'alert' },
  }[status];
  return <span className={`inline-flex items-center gap-1 rounded-pill border px-2 py-0.5 text-[11px] ${map.cls}`}><Icon name={map.icon} size={12} />{map.label}</span>;
}
